// BULK SHEET IMPORT - drop a folder of drawings, map them, import.
//
// Owner, 2026-09-30, verbatim because the shape is in the words: "can't i pick
// the activity from the schedule in the mapping UI, mapping UI will already
// fill all fields based on existing sheets in the project, and from the sheets
// themselves and stays popped up, and the fields that it can't select like
// schedule dropdown, it will leave it empty for user, to click bulk dropdown
// and assign to everyone or each sheet dropdown. also do try to find the
// accurate activity on the schedule for each sheet, usually, its around shop
// drawings release or signoff".
//
// IT STAYS OPEN. Dropping files does not import them - it proposes, and the
// person works down the grid. Nothing is written until Import.
//
// THE ACTIVITY IS THE ONLY FIELD THAT CAN BLOCK, and that is the owner's rule:
// a drawing's due date and sign-off come from its activity, so a drawing
// without one cannot exist. Where the matcher could not decide, the cell is
// RED and empty rather than filled with a guess - a wrong activity is a
// deadline against the wrong work and a sign-off that releases something
// nobody inspected.
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Button, Modal, Select, Table, Tag, Tooltip, Upload, Progress, Input, message, InputNumber,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { leafActivityOptions, LEAF_SELECT_PROPS } from "helpers/schedule/leafActivityOptions";
import { proposeSheets, applySheets, submitShopDrawing } from "api/tracking/shopDrawingsApi";
import { matchOption } from "components/SearchSelect/searchSelect";

/** A content hash, so the import can tell a new revision from the same file. */
async function sha1Of(file) {
  try {
    if (!window.crypto || !window.crypto.subtle || file.size > 256 * 1024 * 1024) return "";
    const buf = await file.arrayBuffer();
    const digest = await window.crypto.subtle.digest("SHA-1", buf);
    return Array.from(new Uint8Array(digest)).map((b) => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    // No hash is a WORSE answer, not a broken one: without it a re-upload of
    // an identical file reads as a new revision. Said in the grid, not hidden.
    return "";
  }
}

const ACTION_TAG = {
  create: { color: "green", text: "new drawing" },
  revise: { color: "blue", text: "new revision" },
  unchanged: { color: "default", text: "same file, nothing to do" },
  refuse: { color: "red", text: "refused" },
};

export default function BulkSheetImportModal({ open, projectId, onClose, onDone }) {
  const [files, setFiles] = useState([]);          // the File objects, by name
  const [rows, setRows] = useState(null);          // what the server proposed, edited in place
  const [activities, setActivities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [progress, setProgress] = useState(null);  // { done, total, failed }
  const [results, setResults] = useState(null);
  const [err, setErr] = useState("");
  // ONE PROPOSE PER DROP, NOT ONE PER FILE.
  //
  // antd's Dragger fires onChange ONCE PER FILE, each time with the whole
  // accumulated list - so dropping two files proposed twice, and dropping two
  // hundred would have hammered the project with two hundred reads of its
  // whole schedule and every filed sheet number. The second answer superseded
  // the first, so nothing looked wrong. My own test counted the calls and
  // found 2 where it expected 1.
  //
  // The changes are collected and the propose runs once they stop arriving.
  const [pending, setPending] = useState(null);
  const timer = useRef(null);

  const reset = () => {
    setFiles([]); setRows(null); setActivities([]); setProgress(null); setResults(null); setErr(""); setPending(null);
  };

  // Paths resolved over the FULL list before anything filters it - an ancestor
  // removed by a filter is an ancestor the walk cannot resolve.
  // THE LOWEST ACTIVITY ONLY, its path beneath (owner, 2026-10-15) - each
  // sheet lands on a row nothing hangs under. A row the matcher proposed that
  // has activities under it still shows, disabled, asking for a lower one.
  const withPaths = useMemo(() => withActivityPaths(
    (activities || []).map((a) => ({ ...a, _id: a.id, parentId: a.parentId || null })),
  ), [activities]);
  const activityOptions = useMemo(() => leafActivityOptions(withPaths), [withPaths]);
  const leafIds = useMemo(() => new Set(activityOptions.map((o) => o.value)), [activityOptions]);
  const optionsFor = (current) => (current && !activityOptions.some((o) => o.value === String(current))
    ? leafActivityOptions(withPaths, { currentId: current }) : activityOptions);

  const read = useCallback(async (fileList) => {
    setReading(true); setErr(""); setResults(null); setProgress(null);
    try {
      const payload = [];
      for (const f of fileList) {
        // eslint-disable-next-line no-await-in-loop
        payload.push({ name: f.name, size: f.size, hash: await sha1Of(f) });
      }
      const out = await proposeSheets(projectId, payload);
      setFiles(fileList);
      setRows((out.rows || []).map((r, i) => ({
        key: `${r.filename}-${i}`,
        ...r,
        // The parser's title is the drawing's name unless the person says
        // otherwise. Kept editable because a filename is not a title.
        name: r.title || r.filename,
      })));
      setActivities(out.activities || []);
    } catch (e) {
      setErr(e.message || "Could not read the drawings");
    } finally {
      setReading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!pending || !pending.length) return undefined;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { read(pending); setPending(null); }, 60);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [pending, read]);

  const setRow = (key, patch) => setRows((list) => (list || []).map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // HOURS ON EVERY ROW (owner, 2026-10-16): the ESTIMATE on each new drawing,
  // and the HOURS SPENT on every file this delivers - the submit route refuses
  // a delivery without them, as an issue resolution is refused (it was sent
  // 0, so every upload after an import was refused).
  const fillAll = (field, value) => {
    if (!(value > 0)) return;
    setRows((list) => (list || []).map((r) => {
      if (field === "est" ? r.action !== "create" : !(r.action === "create" || r.action === "revise")) return r;
      return { ...r, [field]: value };
    }));
  };

  /** Assign one activity to every row that still needs one. */
  const assignAll = (ganttItemId) => {
    if (!ganttItemId) return;
    let n = 0;
    setRows((list) => (list || []).map((r) => {
      if (r.action !== "create") return r;
      n += 1;
      return { ...r, ganttItemId };
    }));
    message.success(`Activity set on ${n} drawing${n === 1 ? "" : "s"}`);
  };

  // THE GATE, COMPUTED HERE RATHER THAN TRUSTED FROM THE SERVER'S FIRST
  // ANSWER, because the person has been editing since. The route re-checks it
  // anyway - this exists so the button is grey before they press it.
  const problems = useMemo(() => (rows || []).filter(
    // A proposed activity with rows under it is not a pick: the lowest one is.
    (r) => (r.action === "create" && (!r.ganttItemId || !leafIds.has(String(r.ganttItemId)) || !String(r.name || "").trim() || !(r.est > 0)))
      || ((r.action === "create" || r.action === "revise") && !(r.spent > 0)),
  ), [rows, leafIds]);
  const willDo = useMemo(() => {
    const n = (a) => (rows || []).filter((r) => r.action === a).length;
    return { create: n("create"), revise: n("revise"), unchanged: n("unchanged"), refuse: n("refuse") };
  }, [rows]);

  const run = async () => {
    setBusy(true); setErr(""); setResults(null);
    try {
      const out = await applySheets(projectId, (rows || []).map((r) => ({
        filename: r.filename, action: r.action, sheetNumber: r.number || "",
        name: r.name, description: "", area: r.area || "", package: r.package || "",
        system: r.system || "", ganttItemId: r.ganttItemId || "",
        ...(r.action === "create" ? { estimatedHours: r.est } : {}),
      })));
      const spentOf = new Map((rows || []).map((r) => [r.filename, r.spent]));
      // THE ROWS EXIST NOW; THE FILES DO NOT. Each one goes through the
      // ordinary submit route, which hashes it, files it into Document
      // Control and bumps the revision - one delivery implementation, not two.
      const todo = [...(out.created || []), ...(out.revised || [])];
      setProgress({ done: 0, total: todo.length, failed: 0 });
      const byName = new Map((files || []).map((f) => [f.name, f]));
      let done = 0; let failed = 0;
      const uploaded = [];
      for (const t of todo) {
        const f = byName.get(t.filename);
        try {
          if (!f) throw new Error("the file is no longer in this dialog");
          // eslint-disable-next-line no-await-in-loop
          await submitShopDrawing(projectId, t.uploadTo, { file: f, hoursSpent: spentOf.get(t.filename) });
          uploaded.push({ ...t, uploaded: true });
        } catch (e) {
          failed += 1;
          uploaded.push({ ...t, uploaded: false, err: e.message || "upload failed" });
        }
        done += 1;
        setProgress({ done, total: todo.length, failed });
      }
      setResults({ ...out, uploaded });
      if (onDone) onDone();
      // DELIBERATELY NOT CLOSED. The owner asked for a window that stays up,
      // and the result of twenty uploads is the one thing worth reading.
    } catch (e) {
      setErr(e.message || "Could not import the drawings");
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    {
      title: "File", dataIndex: "filename", width: 230, ellipsis: true,
      render: (v) => <Tooltip title={v}><span style={{ fontSize: 12 }}>{v}</span></Tooltip>,
    },
    {
      title: "Number", dataIndex: "number", width: 120,
      render: (v, r) => (
        <Tooltip title={r.why && r.why.number}>
          <Input size="small" value={v || ""} placeholder="none"
                 onChange={(e) => setRow(r.key, { number: e.target.value })} />
        </Tooltip>
      ),
    },
    {
      title: "Name", dataIndex: "name", width: 240,
      render: (v, r) => (
        <Input size="small" value={v || ""} status={r.action === "create" && !String(v || "").trim() ? "error" : ""}
               onChange={(e) => setRow(r.key, { name: e.target.value })} />
      ),
    },
    { title: "Lvl", dataIndex: "level", width: 60, render: (v, r) => <Tooltip title={r.why && r.why.level}>{v || <span style={{ color: "#bbb" }}>—</span>}</Tooltip> },
    { title: "Area", dataIndex: "area", width: 70, render: (v, r) => <Tooltip title={r.why && r.why.area}>{v || <span style={{ color: "#bbb" }}>—</span>}</Tooltip> },
    { title: "Rev", dataIndex: "revision", width: 55, render: (v) => (v === null || v === undefined ? <span style={{ color: "#bbb" }}>—</span> : v) },
    {
      title: "What happens", dataIndex: "action", width: 150,
      render: (v, r) => {
        const t = ACTION_TAG[v] || { color: "default", text: v };
        return <Tooltip title={r.actionWhy}><Tag color={t.color} style={{ margin: 0 }}>{t.text}</Tag></Tooltip>;
      },
    },
    {
      title: "Est. hours", dataIndex: "est", width: 90,
      render: (v, r) => (r.action !== "create" ? <span style={{ color: "#bbb" }}>—</span> : (
        <InputNumber size="small" min={0.25} step={0.25} value={v} status={v > 0 ? "" : "error"}
                     aria-label="Estimated hours" style={{ width: "100%" }}
                     onChange={(n) => setRow(r.key, { est: n })} />
      )),
    },
    {
      title: "Hours spent", dataIndex: "spent", width: 90,
      render: (v, r) => (!(r.action === "create" || r.action === "revise") ? <span style={{ color: "#bbb" }}>—</span> : (
        <InputNumber size="small" min={0.25} max={1000} step={0.25} value={v} status={v > 0 ? "" : "error"}
                     aria-label="Hours spent" style={{ width: "100%" }}
                     onChange={(n) => setRow(r.key, { spent: n })} />
      )),
    },
    {
      title: "Schedule activity it signs off", dataIndex: "ganttItemId", width: 320,
      render: (v, r) => {
        if (r.action === "revise") {
          return <span style={{ fontSize: 12, color: "#888" }}>keeps the activity of the drawing it revises</span>;
        }
        if (r.action !== "create") return <span style={{ color: "#bbb" }}>—</span>;
        return (
          <Tooltip title={!v ? (r.activity && r.activity.why) || "pick the activity this drawing signs off" : undefined}>
            <Select filterOption={matchOption} size="small" allowClear {...LEAF_SELECT_PROPS} dropdownMatchSelectWidth={420}
                    style={{ width: "100%" }} value={v || undefined}
                    status={!v || !leafIds.has(String(v)) ? "error" : ""}
                    placeholder="Pick the activity"
                    onChange={(id) => setRow(r.key, { ganttItemId: id || "" })}
                    options={optionsFor(v)} />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <Modal
      title="Import sheets"
      open={open}
      width={1180}
      onCancel={() => { reset(); onClose(); }}
      maskClosable={false}
      footer={[
        <Button key="close" onClick={() => { reset(); onClose(); }}>Close</Button>,
        <Button key="again" onClick={reset} disabled={busy || !rows}>Start again</Button>,
        <Button key="go" type="primary" loading={busy} disabled={!rows || !!problems.length || busy
          || !(willDo.create + willDo.revise)}
                onClick={run}>
          {`Import ${willDo.create + willDo.revise} drawing${willDo.create + willDo.revise === 1 ? "" : "s"}`}
        </Button>,
      ]}
    >
      {err ? <Alert type="error" showIcon message={err} style={{ marginBottom: 10 }} /> : null}
      {!rows ? (
        <Upload.Dragger multiple accept=".pdf,.dwg,.dxf" showUploadList={false} disabled={reading}
                        beforeUpload={() => false}
                        onChange={({ fileList }) => {
                          const real = fileList.map((x) => x.originFileObj || x).filter(Boolean);
                          if (real.length) setPending(real);
                        }}>
          <p className="ant-upload-drag-icon"><InboxOutlined /></p>
          <p className="ant-upload-text">{reading ? "Reading the drawings…" : "Drop the drawings here, or click to choose them"}</p>
          <p className="ant-upload-hint">
            PDF, DWG or DXF. Nothing is imported until you have checked the mapping -
            the number, name, level and area are filled in from the file and from the
            sheets already on this project.
          </p>
        </Upload.Dragger>
      ) : (
        <>
          <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: "#666" }}>Set the activity on every new drawing:</span>
            <Select filterOption={matchOption} size="small" allowClear {...LEAF_SELECT_PROPS} style={{ minWidth: 340 }}
                    placeholder="Assign one activity to all of them"
                    onChange={assignAll} options={activityOptions} />
            <InputNumber size="small" min={0.25} step={0.25} style={{ width: 150 }}
                         placeholder="Est. hours, all new" aria-label="Estimated hours on every new drawing"
                         onChange={(n) => fillAll("est", n)} />
            <InputNumber size="small" min={0.25} max={1000} step={0.25} style={{ width: 150 }}
                         placeholder="Hours spent, all" aria-label="Hours spent on every drawing"
                         onChange={(n) => fillAll("spent", n)} />
            <span style={{ fontSize: 12, color: "#666" }}>
              {willDo.create} new · {willDo.revise} revision{willDo.revise === 1 ? "" : "s"}
              {willDo.unchanged ? ` · ${willDo.unchanged} unchanged` : ""}
              {willDo.refuse ? ` · ${willDo.refuse} refused` : ""}
            </span>
          </div>
          {problems.length ? (
            <Alert type="warning" showIcon style={{ marginBottom: 8 }}
                   message={`${problems.length} drawing${problems.length === 1 ? "" : "s"} still need${problems.length === 1 ? "s" : ""} something - the red cells: an activity and a name (the due date and the sign-off come from the activity), estimated hours on a new drawing, and the hours spent on every file delivered.`} />
          ) : null}
          <Table size="small" rowKey="key" columns={columns} dataSource={rows}
                 pagination={false} scroll={{ y: 380, x: 1320 }} />
          {progress ? (
            <div style={{ marginTop: 10 }}>
              <Progress percent={progress.total ? Math.round((progress.done / progress.total) * 100) : 0}
                        status={progress.failed ? "exception" : (progress.done === progress.total ? "success" : "active")} />
              <div style={{ fontSize: 12, color: "#666" }}>
                {`Uploading ${progress.done} of ${progress.total}${progress.failed ? ` · ${progress.failed} failed` : ""}`}
              </div>
            </div>
          ) : null}
          {results ? (
            <Alert type={results.failed && results.failed.length ? "warning" : "success"} showIcon
                   style={{ marginTop: 10 }}
                   message={`${(results.created || []).length} created, ${(results.revised || []).length} revised, ${(results.skipped || []).length} skipped, ${(results.failed || []).length} failed`}
                   description={(results.failed || []).length ? (
                     <ul style={{ margin: 0, paddingLeft: 18 }}>
                       {results.failed.map((x) => <li key={x.filename}>{`${x.filename}: ${x.err}`}</li>)}
                     </ul>
                   ) : undefined} />
          ) : null}
        </>
      )}
    </Modal>
  );
}
